import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import { useToast } from './ui/ToastProvider';
import { transactionStore, HolidayCalendarChange } from '../utils/transactionStore';
const ACTION_COLOR: Record<string, string> = {
  ADD: '#059669',
  EDIT: '#5B4EF0',
  DELETE: '#EF4444'
};
const ACTION_BG: Record<string, string> = {
  ADD: 'rgba(16,185,129,0.12)',
  EDIT: 'rgba(91,78,240,0.12)',
  DELETE: 'rgba(239,68,68,0.12)'
};
const CheckerHolidayCalendarTable: React.FC = () => {
  const [requests, setRequests] = useState<HolidayCalendarChange[]>([]);
  const [selected, setSelected] = useState<HolidayCalendarChange | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    showToast
  } = useToast();
  const refresh = () => setRequests(transactionStore.getPendingHolidayCalendarChanges());
  useEffect(() => {
    refresh();
    const unsubscribe = transactionStore.subscribe(refresh);
    return unsubscribe;
  }, []);
  const handleAction = (row: HolidayCalendarChange, action: 'approve' | 'reject') => {
    setSelected(row);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selected || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'approve') transactionStore.approveHolidayCalendarChange(selected.id, reason);else transactionStore.rejectHolidayCalendarChange(selected.id, reason);
      showToast(`Holiday ${selected.action.toLowerCase()} ${actionType === 'approve' ? 'approved' : 'rejected'} for "${selected.name}"`, actionType === 'approve' ? 'success' : 'info');
      setActionDialogOpen(false);
      setReason('');
      setSelected(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const rowActions: RowAction<HolidayCalendarChange>[] = [{
    icon: 'lucide:check-circle',
    label: 'Approve',
    onClick: row => handleAction(row, 'approve')
  }, {
    icon: 'lucide:x-circle',
    label: 'Reject',
    onClick: row => handleAction(row, 'reject')
  }];
  const columns: Column<HolidayCalendarChange>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-605" data-mp="2dja6wc67qkb">{row.id}</Typography>
  }, {
    id: 'action',
    label: 'Action',
    renderCell: row => <Chip label={row.action} size="small" sx={{
      fontWeight: 700,
      bgcolor: ACTION_BG[row.action],
      color: ACTION_COLOR[row.action]
    }} data-id="element-606" data-mp="79hzcm5bpcls" />
  }, {
    id: 'name',
    label: 'Holiday Name',
    renderCell: row => <Typography variant="body2" fontWeight={700} noWrap sx={{
      maxWidth: 220
    }} data-id="element-607" data-mp="3tkvql958bzz6">{row.name}</Typography>
  }, {
    id: 'date',
    label: 'Date',
    renderCell: row => <Typography variant="body2" fontWeight={600} data-id="element-608" data-mp="35wpc5s9b3r5o">{row.date}</Typography>
  }, {
    id: 'scope',
    label: 'Scope / Partner',
    renderCell: row => <Typography variant="body2" fontSize="0.82rem" color="text.secondary" data-id="element-609" data-mp="3jtcd064dr1gv">{row.scope === 'partner' ? row.partner : 'All Partners'}</Typography>
  }, {
    id: 'makerReason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 220
    }} data-id="element-610" data-mp="3tr9yiluuuk5s">{row.resubmitReason || row.makerReason}</Typography>
  }, {
    id: 'requestedBy',
    label: 'Requested By',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-611" data-mp="6idciy07iled">{row.requestedBy}</Typography>
  }, {
    id: 'requestedAt',
    label: 'Requested At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-612" data-mp="gfroolf3ko0v">{new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}</Typography>
  }];
  return <>
      <DataTable title="Pending Holiday Calendar Requests" data={requests} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No pending holiday calendar requests" initialVisibleColumns={['action', 'name', 'date', 'scope', 'makerReason', 'requestedBy', 'requestedAt']} data-id="element-613" data-mp="vijedpivgfmw" />

      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-614" data-mp="19aza059g1bds">
        <DialogTitle data-id="element-615" data-mp="gk7gxgnod2uo">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-616" data-mp="glimnm0okkof">
            <Icon icon={actionType === 'approve' ? 'lucide:check-circle' : 'lucide:x-circle'} width={24} color={actionType === 'approve' ? '#10B981' : '#EF4444'} data-id="element-617" data-mp="ljtwvxuw0x76" />
            <Typography variant="h6" fontWeight={600} data-id="element-618" data-mp="qjybtjg5djva">{actionType === 'approve' ? 'Approve' : 'Reject'} Holiday Request</Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-619" data-mp="21wr5x0weukei">
          <Box sx={{
          mt: 1
        }} data-id="element-620" data-mp="21vz8dkj96cll">
            {selected && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-621" data-mp="3mejh4zlvs7sg">
                  {selected.action} &middot; <strong data-id="element-622" data-mp="3hh9wdx9fskay">{selected.name}</strong> &middot; {selected.date}
                </Typography>
                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-623" data-mp="3cogrn9dyxbyv">
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-624" data-mp="37q6u4s4rtza3">Maker's Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-625" data-mp="33qxytiu31tcf">{selected.makerReason}</Typography>
                </Paper>
                {selected.resubmitReason && <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: 'rgba(245, 158, 11, 0.05)',
              borderColor: '#FCD34D'
            }} data-id="element-626" data-mp="2tqdwecutuism">
                    <Typography variant="caption" color="#D97706" fontWeight={600} data-id="element-627" data-mp="2otnh3rqujr89">Previous Rejection Reason</Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-628" data-mp="rxq06nglovmc">{selected.checkerComment}</Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-629" data-mp="mywon0kvjeot">Maker's Resubmit Reason</Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-630" data-mp="2m86ajnkiwz26">{selected.resubmitReason}</Typography>
                  </Paper>}
              </>}
            <TextField label={`Reason for ${actionType === 'approve' ? 'Approval' : 'Rejection'}`} placeholder={`Enter reason for ${actionType === 'approve' ? 'approving' : 'rejecting'} this request...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-631" data-mp="27gxeowq12hgl" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-632" data-mp="2r5zf6c66hl0c">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-633" data-mp="2v8chsrpmp2sw">Cancel</Button>
          <Button variant="contained" color={actionType === 'approve' ? 'success' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-635" data-mp="1juifrennbvw6" /> : <Icon icon={actionType === 'approve' ? 'lucide:check' : 'lucide:x'} width={16} data-id="element-636" data-mp="1c954ta4praa0" />} data-id="element-634" data-mp="1frlufg6q1loj">
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>;
};
export default CheckerHolidayCalendarTable;